import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Spinner from "components/Spinner/Spinner";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import OneButtonCommonLayout from "containers/PageAddListing/OneButtonCommonLayout";
import CommonDialog from "../../components/Dialog/index";
import { commonAxiosPostRequest } from "utils/request";
import { BuyFormInfoCacheLocalStorageKey, CartCacheLocalStorageKey, TokenLocalStorageKey } from "utils/constant";
import { getPureArray, safeParse } from "utils/util";
import { cartToItems, trackPurchase } from "utils/analytics";

export interface PaymentConfirmProps {
	className?: string;
}

type TPaymentResult = {
	id: number;
	/** 0未支付、1支付成功、2支付失败 */
	paymentStatus: 0 | 1 | 2;
	statusDescription: string;
	customId: string;
	paymentMethod: string;
	totalPrice: number;
	expressFee: number;
};

const PaymentConfirmPage: FC<PaymentConfirmProps> = ({ className = "" }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	// 支付结果
	const [paymentResult, setPaymentResult] = useState<null | TPaymentResult>(null);
	const [autoRegisterUserInfo, setAutoRegisterUserInfo] = useState<any>(null);
	// 弹框展示信息
	const [dialogInfo, setDialogInfo] = useState({
		show: false,
		title: '',
		content: '',
	});
	const pollTimerRef = useRef<number | null>(null);
	const MAX_PAYMENT_QUERY_RETRY = 5;
	const MAX_PENDING_POLL = 40;

	const clearPollTimer = () => {
		if (pollTimerRef.current != null) {
			window.clearTimeout(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	};

	// 自动注册新用户时的处理
	const handleAutoRegisterUserOnPaymentComplete = () => {
		const query = new URLSearchParams(window.location.search);
		const email = query.get('email');
		const password = query.get('password');
		if (email) {
			setAutoRegisterUserInfo({ email, password });
		}
	}

	useEffect(() => {
		let cancelled = false;
		let errorAttempt = 0;
		let pendingAttempt = 0;

		const queryPaymentResult = async () => {
			if (cancelled) {
				return;
			}
			clearPollTimer();
			const orderId = new URLSearchParams(window.location.search).get('orderId');
			if (!orderId) {
				setLoading(false);
				setDialogInfo((prev) => ({
					...prev,
					show: true,
					title: t('common.tips'),
					content: '缺少訂單編號',
				}));
				return;
			}
			const [error, response] = await commonAxiosPostRequest<TPaymentResult, {}>('/universal_mall/api/app/product/payment-result', { orderId });
			if (cancelled) {
				return;
			}
			const paymentStatus = response?.paymentStatus;
			const statusKnown = paymentStatus === 0 || paymentStatus === 1 || paymentStatus === 2;
			if (error || !statusKnown) {
				errorAttempt += 1;
				if (errorAttempt >= MAX_PAYMENT_QUERY_RETRY) {
					setLoading(false);
					setDialogInfo((prev) => ({
						...prev,
						show: true,
						title: t('common.tips'),
						content: error?.message || response?.statusDescription || '查詢失敗，請稍後重試',
					}));
					return;
				}
				pollTimerRef.current = window.setTimeout(queryPaymentResult, 3000);
				return;
			}
			errorAttempt = 0;
			if (paymentStatus === 0) {
				pendingAttempt += 1;
				if (pendingAttempt >= MAX_PENDING_POLL) {
					setLoading(false);
					setDialogInfo((prev) => ({
						...prev,
						show: true,
						title: t('common.tips'),
						content: '支付結果確認超時，請稍後重新整理頁面',
					}));
					return;
				}
				pollTimerRef.current = window.setTimeout(queryPaymentResult, 3000);
				return;
			}
			if (paymentStatus === 1 && response) {
				const cachedCart = safeParse(window.localStorage.getItem(CartCacheLocalStorageKey), []);
				const cachedForm = safeParse(window.localStorage.getItem(BuyFormInfoCacheLocalStorageKey), {}) as { couponCode?: string };
				trackPurchase({
					transactionId: orderId,
					value: Number(response.totalPrice) || 0,
					shipping: Number(response.expressFee) || 0,
					payment_type: response.paymentMethod,
					coupon: cachedForm?.couponCode || "",
					items: cartToItems(Array.isArray(cachedCart) ? cachedCart : []),
				});
				setPaymentResult(response);
				setLoading(false);
				window.localStorage.setItem(CartCacheLocalStorageKey, '');
				return;
			}
			setLoading(false);
			setDialogInfo((prev) => ({
				...prev,
				show: true,
				title: t('common.tips'),
				content: response?.statusDescription || '查詢失敗，請稍後重試',
			}));
		};

		queryPaymentResult();
		handleAutoRegisterUserOnPaymentComplete();
		return () => {
			cancelled = true;
			clearPollTimer();
		};
	}, []);

	return (
		<main className="container mt-11 mb-24 flex justify-center lg:flex-row flex-col">

			{
				(loading === false && autoRegisterUserInfo) ? (
					<div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full text-center">
						<h1 className="text-2xl font-semibold text-gray-800 mb-6">{t('user.autoCreateAccountTitle')}</h1>
						<p className="text-gray-700 mb-4">{t('user.autoCreateAccountDesc')}</p>

						<div className="mb-6">
							<label className="block text-sm font-medium text-gray-700 mb-2">{t('user.email')}</label>
							<div className="border rounded-lg px-4 py-2 bg-gray-50 text-gray-900">
								{autoRegisterUserInfo.email}
							</div>
						</div>

						<div className="mb-6">
							<label className="block text-sm font-medium text-gray-700 mb-2">{t('user.password')}</label>
							<div className="border rounded-lg px-4 py-2 bg-gray-50 text-gray-900">
								{autoRegisterUserInfo.password}
							</div>
						</div>

						<p className="text-sm text-gray-600 mb-8">{t('user.autoCreateAccountLogin')}</p>

						<ButtonPrimary href="/login">{t('user.login')}</ButtonPrimary>
					</div>
				) : null
			}

			{loading === true
				? <Spinner className="pt-24 pb-24 lg:py-32" />
				: (
					paymentResult
						? <OneButtonCommonLayout backtHref="/">
							<h2 className="text-3xl lg:text-4xl font-semibold">{t('checkout.thanks_title')}</h2>
							<div className="border-b border-neutral-200 dark:border-neutral-700"></div>
							<div className="text-[20px] lg:text-2xl font-semibold whitespace-normal break-all">
								{`${t('checkout.thanks_desc1')}${paymentResult.customId}${t('checkout.thanks_desc2')}`}
							</div>
						</OneButtonCommonLayout>
						: <OneButtonCommonLayout backtHref="/">
							<h2 className="text-3xl lg:text-4xl font-semibold">{t('checkout.error_title')}</h2>
							<div className="border-b border-neutral-200 dark:border-neutral-700"></div>
							<h3 className="text-2xl font-semibold">{t('checkout.error_desc')}</h3>
						</OneButtonCommonLayout>
				)
			}
			<CommonDialog
				{...dialogInfo}
				onClose={() => setDialogInfo((prev) => ({ ...prev, show: false }))}
			/>
		</main>
	);
};

export default PaymentConfirmPage;
